/* eslint-disable react-refresh/only-export-components --
 * Context module: Provider + hook are intentionally co-located.
 */
import React, { createContext, useContext, useMemo } from 'react';
import type { FormValue, Question, OASISFormData } from '../../types';
import { buildQuestionTreeMap } from '../../walkQuestionTree';
import type { BespokeFormSectionProps } from '../bespokeRegistry';

interface BespokeSectionContextValue {
  answers: Record<string, FormValue>;
  onAnswerChange: (qid: string, value: FormValue) => void;
  onFormDataChange: (updates: Partial<OASISFormData>) => void;
  schemaMap: Map<string, Question>;
  /**
   * Render nothing for a field whose qid the form does not serve in any
   * section, and for a container holding only such fields, as print does.
   * Off in the Forms Manager previews, which keep their developer messages.
   * When on, `schemaMap` also holds the rest of the form's questions.
   */
  hideFieldsMissingFromSchema: boolean;
}

const BespokeSectionContext = createContext<BespokeSectionContextValue | null>(
  null
);

export function useBespokeSectionContext(): BespokeSectionContextValue {
  const ctx = useContext(BespokeSectionContext);
  if (!ctx)
    throw new Error(
      'OASIS primitives must be used inside <BespokeSectionProvider>'
    );
  return ctx;
}

type BespokeSectionProviderProps = BespokeFormSectionProps & {
  hideFieldsMissingFromSchema?: boolean;
  /** Prebuilt by `buildSectionSchemaMap`, so totals and fields share one map. */
  schemaMap?: Map<string, Question>;
  children: React.ReactNode;
};

export function buildSchemaMap(questions: Question[]): Map<string, Question> {
  const map = buildQuestionTreeMap(questions);
  // A completed chart's frozen layout can still ask a question since replaced;
  // its definition travels on the successor's `replaces`. Only this section's
  // successors are seen here; the PDF indexes the whole form.
  for (const question of [...map.values()]) {
    const retired = question.replaces?.question;
    if (!retired) continue;
    for (const [qid, entry] of buildQuestionTreeMap([retired])) {
      if (!map.has(qid)) map.set(qid, entry);
    }
  }
  return map;
}

/**
 * The questions a section's fields resolve against: its own, plus the rest of
 * the form's when hiding is on. This section's definitions win.
 */
export function buildSectionSchemaMap(
  schema: Question[],
  formSchema: Question[] | undefined,
  hideFieldsMissingFromSchema: boolean
): Map<string, Question> {
  const sectionMap = buildSchemaMap(schema);
  if (!hideFieldsMissingFromSchema || !formSchema) return sectionMap;
  return new Map([...buildSchemaMap(formSchema), ...sectionMap]);
}

export function BespokeSectionProvider({
  answers,
  onAnswerChange,
  onFormDataChange,
  schema,
  formSchema,
  hideFieldsMissingFromSchema = false,
  schemaMap: prebuiltSchemaMap,
  children,
}: BespokeSectionProviderProps) {
  const schemaMap = useMemo(
    () =>
      prebuiltSchemaMap ??
      buildSectionSchemaMap(schema, formSchema, hideFieldsMissingFromSchema),
    [prebuiltSchemaMap, schema, formSchema, hideFieldsMissingFromSchema]
  );

  const value = useMemo(
    () => ({
      answers,
      onAnswerChange,
      onFormDataChange,
      schemaMap,
      hideFieldsMissingFromSchema,
    }),
    [
      answers,
      onAnswerChange,
      onFormDataChange,
      schemaMap,
      hideFieldsMissingFromSchema,
    ]
  );

  return (
    <BespokeSectionContext.Provider value={value}>
      {children}
    </BespokeSectionContext.Provider>
  );
}
